import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fade,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Container,
  FormControlLabel,
  Switch,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Palette as PaletteIcon,
  List as ListIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

// Sticky note colors with labels
const noteColors = [
  { color: '#ff7b7b', label: 'Red' },      // Soft red
  { color: '#ffb347', label: 'Orange' },   // Warm orange
  { color: '#fff7b3', label: 'Yellow' },   // Classic sticky note yellow
  { color: '#aec6ff', label: 'Blue' },     // Soft blue
  { color: '#77dd77', label: 'Green' },    // Soft green
  { color: '#ffb3d9', label: 'Pink' }      // Soft pink
];

// Add this helper function at the top of the file
const formatDate = (date) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
};

// Add this helper function at the top with the other helpers
const getInitials = (firstName, lastName) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
};

// Custom hook to detect mobile devices
const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

function SortableNote({ 
  todo, 
  editingTodo, 
  setEditingTodo, 
  onEdit, 
  onColorChange, 
  onToggle, 
  onDelete, 
  onSaveEdit, 
  isArchived, 
  onRestore,
  onMoveUp,
  onMoveDown
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: todo._id });

  const isMobile = useIsMobile();
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: isMobile ? '100%' : '300px',
    margin: isMobile ? '8px 0' : '8px',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Fade in={true}>
        <Paper
          elevation={transform ? 6 : 3}
          sx={{
            p: 2,
            minHeight: { xs: 80, sm: 100 },
            bgcolor: todo.color || '#fff7b3',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        >
          {editingTodo && editingTodo._id === todo._id ? (
            <TextField
              fullWidth
              multiline
              value={editingTodo.text}
              onChange={(e) => setEditingTodo({
                ...editingTodo,
                text: e.target.value
              })}
              onBlur={onSaveEdit}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSaveEdit();
                }
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              sx={{ mb: 2 }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
              {!isArchived && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    mr: 1,
                    mt: isMobile ? 0 : 0.5
                  }}
                >
                  {isMobile && !isArchived && (
                    <>
                      <IconButton 
                        size="small" 
                        onClick={onMoveUp}
                        sx={{ p: 0.5 }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={onMoveDown}
                        sx={{ p: 0.5 }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {!isMobile && (
                    <Box 
                      {...attributes} 
                      {...listeners} 
                      sx={{ 
                        cursor: 'move', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.5,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                  )}
                </Box>
              )}
              <Typography
                sx={{
                  textDecoration: todo.completed ? 'line-through' : 'none',
                  color: 'rgba(0, 0, 0, 0.7)',
                  wordBreak: 'break-word',
                  userSelect: 'none',
                  width: '100%',
                  mt: 0.5
                }}
              >
                {todo.text}
              </Typography>
            </Box>
          )}

          {/* Timestamps */}
          <Box sx={{ 
            mt: 2, 
            mb: 1, 
            fontSize: '0.75rem', 
            color: 'rgba(0, 0, 0, 0.6)',
            userSelect: 'none'
          }}>
            <div>
              Created: {formatDate(todo.createdAt)}
              {todo.createdBy && (
                <> by {getInitials(todo.createdBy.firstName, todo.createdBy.lastName)}</>
              )}
            </div>
            {todo.updatedAt && todo.updatedAt !== todo.createdAt && (
              <div>Updated: {formatDate(todo.updatedAt)}</div>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            {!isArchived ? (
              <>
                <Tooltip title="Toggle Complete">
                  <IconButton size="small" onClick={onToggle}>
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={onEdit}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Change Color">
                  <IconButton size="small" onClick={onColorChange}>
                    <PaletteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Archive">
                  <IconButton size="small" onClick={onDelete}>
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Restore">
                <IconButton 
                  size="small" 
                  onClick={() => onRestore(todo)}
                >
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Fade>
    </div>
  );
}

function TodoApp() {
  const [todos, setTodos] = useState([]);
  const [archivedTodos, setArchivedTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [editingTodo, setEditingTodo] = useState(null);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchTodos();
  }, [token, showArchived]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      
      // Use the authenticated endpoint since this is not a public display
      const endpoint = `${config.apiUrl}/todos${showArchived ? '/archived' : ''}`;
      const headers = { 'Authorization': `Bearer ${token}` };
      console.log('Using authenticated endpoint for todos:', endpoint);
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw todos data from API:', data);
      
      if (showArchived) {
        setArchivedTodos(data);
      } else {
        setTodos(data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching todos:', error);
      setLoading(false);
    }
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    try {
      const response = await fetch(`${config.apiUrl}/todos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: newTodo,
          color: noteColors[0].color,
          order: todos.length,
          createdBy: user._id
        }),
      });
      const data = await response.json();
      setTodos([...todos, data]);
      setNewTodo('');
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

  const updateTodo = async (id, updates) => {
    try {
      const response = await fetch(`${config.apiUrl}/todos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates),
      });
      const updatedTodo = await response.json();
      setTodos(todos.map(t => t._id === id ? updatedTodo : t));
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a more intentional drag to start
      activationConstraint: {
        distance: 8, // 8px
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // First update the state and get the new array
      setTodos((items) => {
        const oldIndex = items.findIndex(item => item._id === active.id);
        const newIndex = items.findIndex(item => item._id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Save the new order to the database
        const saveOrder = async () => {
          try {
            // Map the new array to include order
            const updates = newItems.map((todo, index) => ({
              id: todo._id,
              order: index
            }));

            // Update all todos with their new order
            await Promise.all(updates.map(({ id, order }) => 
              fetch(`${config.apiUrl}/todos/${id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ order })
              })
            ));
          } catch (error) {
            console.error('Error saving todo order:', error);
          }
        };

        // Execute the save operation
        saveOrder();

        // Return the new array for state update
        return newItems;
      });
    }
  };

  const handleColorClick = (event, todoId) => {
    event.stopPropagation();
    setColorMenuAnchor(event.currentTarget);
    setSelectedTodo(todoId);
  };

  const handleColorClose = () => {
    setColorMenuAnchor(null);
    setSelectedTodo(null);
  };

  const handleColorSelect = async (color) => {
    console.log('Color select clicked:', color, 'for todo:', selectedTodo);
    if (selectedTodo) {
      try {
        const response = await fetch(`${config.apiUrl}/todos/${selectedTodo}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            color
          }),
        });
        console.log('Color update response:', response);
        if (!response.ok) throw new Error('Failed to update color');
        const updatedTodo = await response.json();
        console.log('Updated todo:', updatedTodo);
        setTodos(prevTodos => prevTodos.map(t => t._id === selectedTodo ? updatedTodo : t));
      } catch (error) {
        console.error('Error updating todo color:', error);
      }
    }
    handleColorClose();
  };

  const toggleTodo = async (id) => {
    console.log('Toggle clicked for todo:', id);
    try {
      const todo = todos.find(t => t._id === id);
      if (!todo) return;
      console.log('Current todo state:', todo);

      const response = await fetch(`${config.apiUrl}/todos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          completed: !todo.completed
        }),
      });
      console.log('Toggle response:', response);
      if (!response.ok) throw new Error('Failed to toggle todo');
      const updatedTodo = await response.json();
      console.log('Updated todo:', updatedTodo);
      setTodos(prevTodos => prevTodos.map(t => t._id === id ? updatedTodo : t));
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  const startEditing = (todo) => {
    console.log('Start editing todo:', todo);
    setEditingTodo({ ...todo });
  };

  const saveEdit = async () => {
    console.log('Save edit called with:', editingTodo);
    if (editingTodo) {
      try {
        const response = await fetch(`${config.apiUrl}/todos/${editingTodo._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            text: editingTodo.text
          }),
        });
        console.log('Edit response:', response);
        if (!response.ok) throw new Error('Failed to update todo');
        const updatedTodo = await response.json();
        console.log('Updated todo:', updatedTodo);
        setTodos(prevTodos => prevTodos.map(t => t._id === editingTodo._id ? updatedTodo : t));
        setEditingTodo(null);
      } catch (error) {
        console.error('Error updating todo:', error);
      }
    }
  };

  const handleArchive = async (todo) => {
    try {
      console.log('Handling archive/restore for todo:', todo);
      
      // Check if the todo object has the archived property
      if (todo.archived === undefined) {
        console.error('Todo object is missing archived property:', todo);
        throw new Error('Invalid todo object');
      }
      
      const action = todo.archived ? 'restore' : 'archive';
      const url = `${config.apiUrl}/todos/${todo._id}/${action}`;
      
      console.log(`Making ${action} request to: ${url}`);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`${action} API error:`, response.status, errorData);
        throw new Error(`Failed to ${action} todo: ${errorData.error || response.statusText}`);
      }
      
      const updatedTodo = await response.json();
      console.log(`Todo ${action}d successfully:`, updatedTodo);
      
      if (todo.archived) {
        // If we're restoring an archived todo, remove it from archived list and add to active list
        setArchivedTodos(archivedTodos.filter(t => t._id !== todo._id));
        setTodos([...todos, updatedTodo]);
        
        // Fetch both active and archived todos to ensure lists are up to date
        fetchBothTodoLists();
      } else {
        // If we're archiving an active todo, remove it from active list and add to archived list
        setTodos(todos.filter(t => t._id !== todo._id));
        setArchivedTodos([...archivedTodos, updatedTodo]);
        
        // Refetch the todos to ensure lists are up to date
        fetchTodos();
      }
    } catch (error) {
      console.error('Error archiving/restoring todo:', error);
    }
  };

  // Helper function to fetch both active and archived todos
  const fetchBothTodoLists = async () => {
    try {
      setLoading(true);
      
      // Fetch active todos
      const activeEndpoint = `${config.apiUrl}/todos`;
      const archivedEndpoint = `${config.apiUrl}/todos/archived`;
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [activeResponse, archivedResponse] = await Promise.all([
        fetch(activeEndpoint, { headers }),
        fetch(archivedEndpoint, { headers })
      ]);
      
      if (!activeResponse.ok || !archivedResponse.ok) {
        throw new Error('Failed to fetch todos');
      }
      
      const [activeData, archivedData] = await Promise.all([
        activeResponse.json(),
        archivedResponse.json()
      ]);
      
      setTodos(activeData);
      setArchivedTodos(archivedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching todos:', error);
      setLoading(false);
    }
  };

  // Function to move a todo up in the list
  const handleMoveUp = useCallback(async (todoId) => {
    const currentIndex = todos.findIndex(todo => todo._id === todoId);
    if (currentIndex <= 0) return; // Already at the top
    
    const newIndex = currentIndex - 1;
    const newItems = arrayMove(todos, currentIndex, newIndex);
    
    setTodos(newItems);
    
    // Save the new order to the database
    try {
      // Map the new array to include order
      const updates = newItems.map((todo, index) => ({
        id: todo._id,
        order: index
      }));

      // Update all todos with their new order
      await Promise.all(updates.map(({ id, order }) => 
        fetch(`${config.apiUrl}/todos/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ order })
        })
      ));
    } catch (error) {
      console.error('Error updating todo order:', error);
    }
  }, [todos, token]);

  // Function to move a todo down in the list
  const handleMoveDown = useCallback(async (todoId) => {
    const currentIndex = todos.findIndex(todo => todo._id === todoId);
    if (currentIndex === -1 || currentIndex >= todos.length - 1) return; // Already at the bottom
    
    const newIndex = currentIndex + 1;
    const newItems = arrayMove(todos, currentIndex, newIndex);
    
    setTodos(newItems);
    
    // Save the new order to the database
    try {
      // Map the new array to include order
      const updates = newItems.map((todo, index) => ({
        id: todo._id,
        order: index
      }));

      // Update all todos with their new order
      await Promise.all(updates.map(({ id, order }) => 
        fetch(`${config.apiUrl}/todos/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ order })
        })
      ));
    } catch (error) {
      console.error('Error updating todo order:', error);
    }
  }, [todos, token]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          Todo List
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                color="primary"
              />
            }
            label="Show Archived"
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!showArchived && (
            <form onSubmit={addTodo}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: { xs: '100%', sm: 600 },
                  width: '100%',
                  bgcolor: '#fff7b3',  // Keep the yellow color
                  transition: 'transform 0.2s'
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Add a new todo..."
                  value={newTodo}
                  onChange={(e) => setNewTodo(e.target.value)}
                  sx={{ 
                    mr: 1,
                    '& .MuiInput-underline:before': {
                      borderBottomColor: 'rgba(0, 0, 0, 0.3)'  // Softer underline
                    }
                  }}
                />
                <IconButton type="submit" color="primary">
                  <AddIcon />
                </IconButton>
              </Paper>
            </form>
          )}

          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2,
              minHeight: '100px',
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <SortableContext items={(showArchived ? archivedTodos : todos).map(t => t._id)}>
                {(showArchived ? archivedTodos : todos).map((todo) => (
                  <SortableNote
                    key={todo._id}
                    todo={todo}
                    editingTodo={editingTodo}
                    setEditingTodo={setEditingTodo}
                    onEdit={() => startEditing(todo)}
                    onColorChange={(e) => handleColorClick(e, todo._id)}
                    onToggle={() => toggleTodo(todo._id)}
                    onDelete={() => handleArchive(todo)}
                    onSaveEdit={saveEdit}
                    isArchived={showArchived}
                    onRestore={handleArchive}
                    onMoveUp={() => handleMoveUp(todo._id)}
                    onMoveDown={() => handleMoveDown(todo._id)}
                  />
                ))}
              </SortableContext>
            </Box>
          </DndContext>
        </>
      )}

      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={handleColorClose}
        onClick={(e) => e.stopPropagation()}
      >
        {noteColors.map((noteColor) => (
          <MenuItem
            key={noteColor.color}
            onClick={() => handleColorSelect(noteColor.color)}
            sx={{
              backgroundColor: noteColor.color,
              '&:hover': {
                backgroundColor: noteColor.color,
                filter: 'brightness(95%)',
              },
              minWidth: 100,
            }}
          >
            {noteColor.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default TodoApp; 